// ValidationPill — the shared exceptions-only validation pill for the finance
// invoice surfaces. Given a ValidationKind + label it renders a compact badge
// carrying the semantic colour + a glyph; the clean kinds (`ok` / `none`)
// render nothing so every call site stays silent when there is no exception to
// surface. Used by the invoice queue (the aggregate `derived.pill`) and the row
// detail (each charge's `derived.price` result).
//
// Colour mapping mirrors the finance validation vocabulary: `over` is a
// hard-red "needs attention" (overdrawn / overpaying), `under` and `warn` are
// amber heads-ups (under agreed price / line near limit). Red uses AlertTriangle,
// under uses a down-caret, warn uses AlertTriangle — colour is never the only
// channel.

import { AlertTriangle, ArrowDown } from "lucide-react";
import type { ValidationKind } from "../../../utils/financeCalc";

interface ValidationPillProps {
  kind: ValidationKind;
  label: string;
}

type SignalKind = "over" | "under" | "warn";

const PILL_CLASS: Record<SignalKind, string> = {
  over: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  under: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function isSignal(kind: ValidationKind): kind is SignalKind {
  return kind === "over" || kind === "under" || kind === "warn";
}

export default function ValidationPill({ kind, label }: ValidationPillProps) {
  if (!isSignal(kind)) return null;
  const Glyph = kind === "under" ? ArrowDown : AlertTriangle;
  return (
    <span
      data-testid="validation-pill"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium ${PILL_CLASS[kind]}`}
    >
      <Glyph className="h-3 w-3 shrink-0" aria-hidden="true" />
      {label}
    </span>
  );
}
